/*
 * Harbor Saunas — ad click capture for harborsaunas.com (Shopify).
 *
 * Install: upload this file as the theme asset harbor-attribution.js and load it
 * from layout/theme.liquid, just before </body>:
 *   <script src="{{ 'harbor-attribution.js' | asset_url }}" defer></script>
 * Try it on a duplicate, unpublished theme first (docs/ad-attribution.md walks
 * through it).
 *
 * What it does, all in the visitor's own browser:
 *   1. When a page is opened from a Google, Microsoft or Meta ad (its URL carries
 *      gclid, msclkid, a paid utm_medium… — the KEYS below), it remembers those
 *      parameters and the time in localStorage, for up to 90 days (Google's longest
 *      click window). A later ad click replaces an earlier one. A visit that isn't
 *      an ad click — an email campaign's UTM tags, the fbclid on every link out of
 *      Facebook, an internal tag, a reload — never does.
 *   2. On the contact forms (the New Project Inquiry and trade forms, which Shopify
 *      marks form_type "contact") it adds a hidden "Attribution" field, so Shopify's
 *      notification email carries the click to Harbor Ops. The footer's newsletter
 *      sign-up (form_type "customer") is left alone.
 *   3. It saves the same payload on the cart as the hidden attribute
 *      "_harbor_attribution", which Shopify copies onto the order.
 *
 * It sends nothing anywhere else and loads nothing. Every step is wrapped so a
 * failure can never break the page or the form.
 *
 * KEYS must match AD_PARAM_KEYS in src/lib/leads/attribution.ts, and each value is
 * cleaned the way Ops cleans it (cleanValue), so what this script calls an ad click
 * Ops does too; snippet.test.ts fails if either drifts.
 *
 * To check it on a page: open the browser console and run
 *   harborAttribution.payload()
 */
(function (win, doc) {
  "use strict";
  try {
    var VERSION = "1";
    var STORE_KEY = "harbor_attribution";
    var CART_MARK_KEY = "harbor_attribution_cart";
    var CART_ATTRIBUTE = "_harbor_attribution";
    var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
    var CLOCK_SLACK_MS = 5 * 60 * 1000;
    var CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"];
    // The click IDs that name one click. A gbraid or wbraid can be shared by many.
    var ONE_CLICK_KEYS = ["gclid", "msclkid", "fbclid"];
    var KEYS = CLICK_KEYS.concat([
      "gad_source",
      "gad_campaignid",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "adgroupid",
      "assetgroupid",
      "matchtype",
      "network",
    ]);
    var FLAGS = ["gpc", "sale_of_data", "marketing"];
    var CLICK_ID = /^[A-Za-z0-9_-]{1,500}$/;
    var PAID_MEDIUM = /^(cpc|ppc|paid|paid_search|paidsearch|paid_social|paidsocial)$/i;
    // Control and formatting characters, which Ops' cleanText turns into spaces. A
    // browser without Unicode property escapes gets the same set spelled out
    // (Unicode 17's, the astral ones as surrogate pairs).
    var UNPRINTABLE = (function () {
      try {
        return new RegExp("[\\p{Cc}\\p{Cf}]", "gu");
      } catch (e) {
        return /[\u0000-\u001f\u007f-\u009f\u00ad\u0600-\u0605\u061c\u06dd\u070f\u0890\u0891\u08e2\u180e\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u206f\ufeff\ufff9-\ufffb]|\ud804[\udcbd\udccd]|\ud80d[\udc30-\udc3f]|\ud82f[\udca0-\udca3]|\ud834[\udd73-\udd7a]|\udb40[\udc01\udc20-\udc7f]/g;
      }
    })();

    var storage = (function () {
      try {
        return win.localStorage;
      } catch (e) {
        return null;
      }
    })();

    function read(key) {
      try {
        return storage ? storage.getItem(key) : null;
      } catch (e) {
        return null;
      }
    }

    function write(key, value) {
      try {
        if (storage) storage.setItem(key, value);
      } catch (e) {
        /* private mode or full storage: carry on without memory */
      }
    }

    function has(obj, key) {
      return Object.prototype.hasOwnProperty.call(obj, key);
    }

    // A value as Ops reads it (src/lib/leads/attribution.ts cleanParam): a click ID
    // trimmed and token-shaped or nothing, anything else cleaned of unprintable
    // characters. Ops caps the length itself.
    function cleanValue(key, raw) {
      var value = String(raw);
      if (CLICK_KEYS.indexOf(key) !== -1) {
        value = value.trim();
        return CLICK_ID.test(value) ? value : "";
      }
      return value.replace(UNPRINTABLE, " ").replace(/\s+/g, " ").trim();
    }

    function cleanParams(source, get) {
      var params = {};
      var any = false;
      for (var i = 0; i < KEYS.length; i++) {
        var raw = get(source, KEYS[i]);
        var value = typeof raw === "string" ? cleanValue(KEYS[i], raw) : "";
        if (!value) continue;
        params[KEYS[i]] = value;
        any = true;
      }
      return any ? params : null;
    }

    function isAdClick(p) {
      if (p.gclid || p.gbraid || p.wbraid || p.msclkid) return true;
      var paid = PAID_MEDIUM.test(p.utm_medium || "");
      // fbclid says the click came from Facebook or Instagram, whatever the tags say.
      if (p.fbclid) return paid;
      if (p.gad_source) return true;
      return paid && /^(google|bing|microsoft|facebook|instagram|meta|fb|ig)$/.test(String(p.utm_source || "").toLowerCase());
    }

    // The same click landing again (a restored tab, the ad's URL opened twice). Tags
    // alone, or a gbraid, can't tell one click from the next.
    function sameClick(a, b) {
      for (var i = 0; i < ONE_CLICK_KEYS.length; i++) {
        if (a[ONE_CLICK_KEYS[i]] && a[ONE_CLICK_KEYS[i]] === b[ONE_CLICK_KEYS[i]]) return true;
      }
      return false;
    }

    // A remembered click still inside the window, re-cleaned by today's rules, or null.
    // A click stored by an earlier version of this script is held to the same rules
    // as a new one, so nothing Ops can't read is ever sent. One stamped in the future
    // (the clock was set back since) would outrank every real click, so it's dropped.
    function fresh(touch, now) {
      if (!touch || typeof touch.at !== "string" || !touch.p || typeof touch.p !== "object") return null;
      var age = now - Date.parse(touch.at);
      if (!(age < MAX_AGE_MS && age > -CLOCK_SLACK_MS)) return null;
      var p = cleanParams(touch.p, function (o, key) {
        return has(o, key) ? o[key] : null;
      });
      return p && isAdClick(p) ? { p: p, at: touch.at } : null;
    }

    // This page's view of the remembered click and consent. Another tab may have
    // stored a newer click or answer since this page loaded, so sync() merges the
    // stored copy in before each use and each write: the newer click wins, and the
    // stored answers come in under whatever this page reads afresh.
    var state = { v: VERSION, last: null, consent: {} };

    function sync() {
      var saved = null;
      try {
        saved = JSON.parse(read(STORE_KEY) || "null");
      } catch (e) {
        saved = null;
      }
      var mine = saved && saved.v === VERSION;
      var now = Date.now();
      var own = fresh(state.last, now);
      var theirs = mine ? fresh(saved.last, now) : null;
      state.last = !theirs || (own && Date.parse(own.at) >= Date.parse(theirs.at)) ? own : theirs;
      var consent = mine && saved.consent && typeof saved.consent === "object" ? saved.consent : {};
      for (var i = 0; i < FLAGS.length; i++) {
        var answer = consent[FLAGS[i]];
        if (answer === "0" || answer === "1") state.consent[FLAGS[i]] = answer;
      }
    }

    // A reload, or a visit through back/forward, replays a landing URL the visitor
    // already arrived on: its click is the one remembered then, not a new one.
    function replayed() {
      try {
        var perf = win.performance;
        var nav = perf && perf.getEntriesByType ? perf.getEntriesByType("navigation")[0] : null;
        if (nav && nav.type) return nav.type === "reload" || nav.type === "back_forward";
        return !!(perf && perf.navigation) && (perf.navigation.type === 1 || perf.navigation.type === 2);
      } catch (e) {
        return false;
      }
    }

    // Remember this visit when it was a new ad click. The same click landing again (a
    // restored tab) keeps the time it was first seen. A replayed page's click is taken
    // only when nothing is remembered (storage blocked, or cleared since): it's still
    // the click this visitor came from.
    sync();
    var landed = state.last && replayed()
      ? null
      : cleanParams(new win.URLSearchParams((win.location && win.location.search) || ""), function (q, key) {
          return q.get(key);
        });
    if (landed && isAdClick(landed) && !(state.last && sameClick(state.last.p, landed))) {
      state.last = { p: landed, at: new Date().toISOString() };
    }
    write(STORE_KEY, JSON.stringify(state));

    // The newest answer this page heard from Shopify's cookie banner. The privacy API
    // may not reflect it yet when the event fires, so the event's own word wins here
    // until the API reports it too; after that the API's later answers stand.
    var heard = {};

    // The consent the browser reports now, merged into what it reported before.
    // Shopify's privacy API loads on its own schedule, so a page that can't read it
    // keeps the last known answer rather than dropping it; a page that can read a
    // changed answer (an opt-out) replaces it.
    function rememberConsent() {
      var reported = {};
      try {
        var gpc = win.navigator && win.navigator.globalPrivacyControl;
        if (gpc === true) reported.gpc = "1";
        else if (gpc === false) reported.gpc = "0";
      } catch (e) {
        /* not reported */
      }
      try {
        var privacy = win.Shopify && win.Shopify.customerPrivacy;
        if (privacy && typeof privacy.saleOfDataAllowed === "function") {
          reported.sale_of_data = privacy.saleOfDataAllowed() ? "1" : "0";
        }
        if (privacy && typeof privacy.marketingAllowed === "function") {
          reported.marketing = privacy.marketingAllowed() ? "1" : "0";
        }
      } catch (e) {
        /* not reported */
      }
      for (var key in heard) {
        if (!has(heard, key)) continue;
        if (reported[key] === heard[key]) delete heard[key];
        else reported[key] = heard[key];
      }
      sync();
      for (var flag in reported) {
        if (has(reported, flag)) state.consent[flag] = reported[flag];
      }
      var json = JSON.stringify(state);
      if (json !== read(STORE_KEY)) write(STORE_KEY, json);
    }
    rememberConsent();

    // What Ops reads (src/lib/leads/attribution.ts parseAttribution). Always carries
    // v=1, so Ops can tell "no ad click" from "script not installed".
    function payload() {
      rememberConsent();
      var query = new win.URLSearchParams();
      query.set("v", VERSION);
      if (state.last) {
        for (var i = 0; i < KEYS.length; i++) {
          if (has(state.last.p, KEYS[i])) query.set(KEYS[i], state.last.p[KEYS[i]]);
        }
        query.set("landed_at", state.last.at);
      }
      for (var j = 0; j < FLAGS.length; j++) {
        if (has(state.consent, FLAGS[j])) query.set(FLAGS[j], state.consent[FLAGS[j]]);
      }
      return query.toString();
    }

    // The contact forms, found by Shopify's own form_type marker rather than their
    // action, which a translated store prefixes (/fr/contact). A hidden field placed
    // first, so Shopify's notification lists it before the customer's message.
    function attachToForms() {
      var forms = doc.querySelectorAll("form");
      for (var i = 0; i < forms.length; i++) {
        var form = forms[i];
        if (!form.querySelector('input[name="form_type"][value="contact"]')) continue;
        if (!form.querySelector('[name="contact[email]"]')) continue;
        var input = form.querySelector('input[name="contact[Attribution]"]');
        if (!input) {
          input = doc.createElement("input");
          input.type = "hidden";
          input.name = "contact[Attribution]";
          form.insertBefore(input, form.firstChild);
          form.addEventListener("submit", refreshOnSubmit);
        }
        input.value = payload();
      }
    }

    function refreshOnSubmit(event) {
      try {
        var field = event.currentTarget.querySelector('input[name="contact[Attribution]"]');
        if (field) field.value = payload();
      } catch (e) {
        /* the form still submits */
      }
    }

    // The cart: saved once per payload and cart, so it isn't re-posted on every page.
    function cartToken() {
      var match = /(?:^|;\s*)cart=([^;]*)/.exec(doc.cookie || "");
      return match ? match[1] : "";
    }

    // Saved while a click is remembered, and again whenever the payload changes. With
    // no click remembered, only a cart this browser already wrote takes the payload (an
    // opt-out after its click expired): any other cart may hold a click this browser
    // has since lost (Safari clears storage after 7 days), which it would erase.
    function saveOnCart() {
      if (typeof win.fetch !== "function") return;
      var value = payload();
      var token = cartToken();
      var mark = read(CART_MARK_KEY) || "";
      if (!state.last && !(token && mark.indexOf(token + "|") === 0)) return;
      if (mark === token + "|" + value) return;
      var attributes = {};
      attributes[CART_ATTRIBUTE] = value;
      win
        .fetch("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ attributes: attributes }),
        })
        .then(function (res) {
          // The cart cookie may be new now, so the mark reads it after the update.
          if (res && res.ok) write(CART_MARK_KEY, cartToken() + "|" + value);
        })
        .catch(function () {
          /* next page load tries again */
        });
    }

    function run() {
      try {
        attachToForms();
      } catch (e) {
        /* never break the page */
      }
      try {
        saveOnCart();
      } catch (e) {
        /* never break the page */
      }
    }

    win.harborAttribution = { keys: KEYS.slice(), payload: payload };

    if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", run);
    else run();
    // Shopify's cookie banner announces a changed answer. Checkout runs none of this
    // script, so the form field and the cart take it now, not on the next page.
    doc.addEventListener("visitorConsentCollected", function (event) {
      try {
        var detail = event && event.detail;
        if (detail && typeof detail.saleOfDataAllowed === "boolean") heard.sale_of_data = detail.saleOfDataAllowed ? "1" : "0";
        if (detail && typeof detail.marketingAllowed === "boolean") heard.marketing = detail.marketingAllowed ? "1" : "0";
      } catch (e) {
        /* the API's own answer stands */
      }
      run();
    });
  } catch (e) {
    /* never break the page */
  }
})(window, document);
